import React, { useState, useEffect } from 'react';

// ============================================
// SPONSY WEBSITE - CLEAN VERSION
// ============================================

// Simple router hook
function useRouter() {
  const [path, setPath] = useState(window.location.pathname);
  
  useEffect(() => {
    const handlePopState = () => setPath(window.location.pathname);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = (newPath) => {
    window.history.pushState({}, '', newPath);
    setPath(newPath);
  };

  return { path, navigate };
}

// Color constant
const LIME = '#BFFF00';

// ============ ICONS ============
const Icon = ({ children, size = 24, fill = "none" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);

const ArrowRight = ({ size }) => <Icon size={size}><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></Icon>;
const ArrowUpRight = ({ size }) => <Icon size={size}><line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/></Icon>;
const Check = ({ size }) => <Icon size={size}><polyline points="20 6 9 17 4 12"/></Icon>;
const X = ({ size }) => <Icon size={size}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></Icon>;
const Play = ({ size }) => <Icon size={size}><polygon points="5 3 19 12 5 21 5 3"/></Icon>;
const Menu = ({ size }) => <Icon size={size}><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="18" x2="20" y2="18"/></Icon>;
const Star = ({ size, fill, stroke }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill={fill || "none"} stroke={stroke || "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);
const Shield = ({ size }) => <Icon size={size}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></Icon>;
const Users = ({ size }) => <Icon size={size}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></Icon>;
const BarChart = ({ size }) => <Icon size={size}><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></Icon>;
const DollarSign = ({ size }) => <Icon size={size}><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></Icon>;
const FileText = ({ size }) => <Icon size={size}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></Icon>;
const Layout = ({ size }) => <Icon size={size}><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></Icon>;
const Target = ({ size }) => <Icon size={size}><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></Icon>;
const Sparkles = ({ size }) => <Icon size={size}><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/><path d="M5 3v4"/><path d="M3 5h4"/><path d="M19 17v4"/><path d="M17 19h4"/></Icon>;
const Lock = ({ size }) => <Icon size={size}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></Icon>;
const Globe = ({ size }) => <Icon size={size}><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></Icon>;
const Headphones = ({ size }) => <Icon size={size}><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></Icon>;
const Building = ({ size }) => <Icon size={size}><rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/></Icon>;
const ChevronDown = ({ size }) => <Icon size={size}><polyline points="6 9 12 15 18 9"/></Icon>;
const Youtube = ({ size }) => <Icon size={size}><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"/><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"/></Icon>;
const Twitch = ({ size }) => <Icon size={size}><path d="M21 2H3v16h5v4l4-4h5l4-4V2zm-10 9V7m5 4V7"/></Icon>;
const Twitter = ({ size }) => <Icon size={size}><path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"/></Icon>;
const Instagram = ({ size }) => <Icon size={size}><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></Icon>;
const Tiktok = ({ size }) => <Icon size={size}><path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"/></Icon>;

// ============ LOGO ============
function Logo({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
      <rect width="40" height="40" rx="10" fill={LIME}/>
      <path d="M10 12h8c4 0 7 3 7 7s-3 7-7 7h-8V12z" stroke="#0a0a0a" strokeWidth="3" fill="none"/>
      <path d="M25 12l5 7-5 7" stroke="#0a0a0a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// ============ MARQUEE ============
function Marquee({ children, speed = 30 }) {
  return (
    <div className="overflow-hidden whitespace-nowrap">
      <div className="inline-flex animate-marquee" style={{ animationDuration: speed + 's' }}>
        {children}
        {children}
      </div>
    </div>
  );
}

// App URL - production app URL
const APP_URL = 'https://www.sponsy.app';

// ============ NAVBAR ============
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-black/90 backdrop-blur-xl border-b border-white/5' : ''}`}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          <a href="/" className="flex items-center gap-2.5">
            <Logo size={32} />
            <span className="text-lg font-bold text-white">Sponsy</span>
          </a>
          
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-neutral-400 hover:text-white transition-colors text-sm font-medium">Features</a>
            <a href="#how-it-works" className="text-neutral-400 hover:text-white transition-colors text-sm font-medium">How It Works</a>
            <a href="#pricing" className="text-neutral-400 hover:text-white transition-colors text-sm font-medium">Pricing</a>
            <a href="#testimonials" className="text-neutral-400 hover:text-white transition-colors text-sm font-medium">Creators</a>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a href={APP_URL} className="px-4 py-2 text-neutral-300 hover:text-white transition-colors text-sm font-medium">Log In</a>
            <a href={`${APP_URL}?signup=true`} className="px-5 py-2.5 bg-lime-400 text-black font-semibold rounded-full text-sm hover:bg-lime-300 transition-all flex items-center gap-2">
              Get Started
              <ArrowUpRight size={16} />
            </a>
          </div>

          <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden p-2 text-white">
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden py-4 border-t border-white/10">
            <div className="flex flex-col gap-2">
              <a href="#features" className="text-neutral-200 py-2 font-medium">Features</a>
              <a href="#how-it-works" className="text-neutral-200 py-2 font-medium">How It Works</a>
              <a href="#pricing" className="text-neutral-200 py-2 font-medium">Pricing</a>
              <a href="#testimonials" className="text-neutral-200 py-2 font-medium">Creators</a>
            </div>
            <div className="flex gap-3 mt-4 pt-4 border-t border-white/10">
              <a href={APP_URL} className="flex-1 py-3 border border-neutral-600 text-white rounded-full text-sm font-medium text-center">Log In</a>
              <a href={`${APP_URL}?signup=true`} className="flex-1 py-3 bg-lime-400 text-black rounded-full text-sm font-bold text-center">Get Started</a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

// ============ HERO ============
function Hero() {
  const deals = [
    { creator: 'Jake', amount: 12500, platform: 'YouTube' },
    { creator: 'Maria', amount: 8200, platform: 'TikTok' },
    { creator: 'Alex', amount: 15800, platform: 'Twitch' },
    { creator: 'Sam', amount: 6400, platform: 'Instagram' },
    { creator: 'Chris', amount: 22100, platform: 'YouTube' },
    { creator: 'Emma', amount: 9800, platform: 'TikTok' },
  ];

  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-black pt-20 pb-16">
      {/* Grid background */}
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'linear-gradient(rgba(191, 255, 0, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(191, 255, 0, 0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }} 
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-transparent to-black opacity-70" />
      </div>
      
      <div className="relative max-w-7xl mx-auto px-6 w-full">
        {/* Badge */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-3 px-4 py-2 bg-white/5 border border-white/10 rounded-full">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-lime-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-lime-400"></span>
            </span>
            <span className="text-sm text-neutral-200">Trusted by 2,800+ creators worldwide</span>
          </div>
        </div>

        {/* Headline */}
        <h1 className="text-center mb-8">
          <span className="block text-5xl md:text-7xl font-bold text-white leading-tight tracking-tight">
            Stop chasing payments.
          </span>
          <span className="block text-5xl md:text-7xl font-bold text-lime-400 leading-tight tracking-tight mt-2">
            Start closing deals.
          </span>
        </h1>

        {/* Subheadline */}
        <p className="text-center text-lg md:text-xl text-neutral-300 max-w-2xl mx-auto mb-10 leading-relaxed">
          The all-in-one CRM built for content creators. Track sponsorships, manage brands, send invoices, and keep <span className="text-white font-semibold">100% of your earnings</span>.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
          <a href={`${APP_URL}?signup=true`} className="group w-full sm:w-auto px-8 py-4 bg-lime-400 text-black font-bold rounded-full text-base hover:bg-lime-300 transition-all flex items-center justify-center gap-2 shadow-lg shadow-lime-400/20">
            Start Free — No Card Required
            <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
          </a>
          <a href="#how-it-works" className="group w-full sm:w-auto px-8 py-4 bg-white/5 border border-white/10 hover:bg-white/10 text-white font-semibold rounded-full text-base transition-all flex items-center justify-center gap-3">
            <span className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
              <Play size={14} className="text-white ml-0.5" />
            </span>
            Watch Demo
          </a>
        </div>

        {/* Live ticker */}
        <div className="relative mb-16">
          <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-black to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-black to-transparent z-10 pointer-events-none" />
          <Marquee speed={40}>
            <div className="flex gap-4 px-2">
              {[...deals, ...deals].map((deal, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-2.5 bg-neutral-900 border border-neutral-800 rounded-full">
                  <div className="w-7 h-7 rounded-full bg-lime-400 flex items-center justify-center text-black text-xs font-bold">
                    {deal.creator[0]}
                  </div>
                  <span className="text-neutral-300 text-sm">{deal.creator} closed</span>
                  <span className="font-bold text-white">${deal.amount.toLocaleString()}</span>
                  <span className="text-neutral-500 text-sm">• {deal.platform}</span>
                </div>
              ))}
            </div>
          </Marquee>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-4xl mx-auto">
          {[
            { value: '2,847', label: 'Active Creators', IconComp: Users },
            { value: '$5.2M', label: 'Deals Tracked', IconComp: DollarSign, highlight: true },
            { value: '12,400+', label: 'Brands Connected', IconComp: Building },
            { value: '0%', label: 'Commission Forever', IconComp: Target },
          ].map((stat, i) => (
            <div key={i} className="text-center p-5 rounded-2xl bg-neutral-900/80 border border-neutral-800">
              <div className={`w-10 h-10 rounded-xl mx-auto mb-3 flex items-center justify-center ${stat.highlight ? 'bg-lime-400' : 'bg-neutral-800'}`}>
                <stat.IconComp size={20} className={stat.highlight ? 'text-black' : 'text-neutral-300'} />
              </div>
              <div className={`text-2xl md:text-3xl font-bold mb-1 ${stat.highlight ? 'text-lime-400' : 'text-white'}`}>
                {stat.value}
              </div>
              <div className="text-neutral-500 text-sm">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============ PLATFORMS ============
function Platforms() {
  const platforms = [
    { name: 'YouTube', IconComp: Youtube },
    { name: 'Twitch', IconComp: Twitch },
    { name: 'TikTok', IconComp: Tiktok },
    { name: 'Instagram', IconComp: Instagram },
    { name: 'Twitter/X', IconComp: Twitter },
  ];

  return (
    <section className="py-12 md:py-16 bg-black border-y border-neutral-900">
      <div className="max-w-7xl mx-auto px-6">
        <p className="text-center text-neutral-500 text-sm uppercase tracking-widest mb-8">
          Built for creators on every platform
        </p>
        <div className="flex flex-wrap items-center justify-center gap-6 md:gap-12">
          {platforms.map((platform, i) => (
            <div key={i} className="flex items-center gap-2.5 text-neutral-500 hover:text-neutral-300 transition-colors">
              <platform.IconComp size={22} />
              <span className="text-base font-medium">{platform.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============ PROBLEM ============
function Problem() {
  const painPoints = [
    { before: "Spreadsheet chaos", after: "One clean dashboard" },
    { before: "Chasing payments for months", after: "Get paid in days" },
    { before: "Guessing your rates", after: "Data-backed benchmarks" },
    { before: "Lost contacts & emails", after: "Complete brand CRM" },
  ];

  return (
    <section className="py-20 md:py-28 bg-black">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <div className="inline-flex px-4 py-1.5 rounded-full text-sm font-medium mb-6 bg-lime-400/20 text-lime-300">
              The Problem
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
              You're a creator,<br />
              <span className="text-neutral-500">not an accountant.</span>
            </h2>
            <p className="text-lg text-neutral-400 mb-10 leading-relaxed">
              Managing sponsorships shouldn't require a finance degree. Yet here you are — juggling spreadsheets, losing track of emails, and wondering if you're charging enough.
            </p>
            
            <div className="space-y-3">
              {painPoints.map((item, i) => (
                <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-neutral-900/60 border border-neutral-800">
                  <div className="flex items-center gap-2.5 flex-1">
                    <X size={16} className="text-red-400 shrink-0" />
                    <span className="text-neutral-500 line-through text-sm">{item.before}</span>
                  </div>
                  <ArrowRight size={16} className="text-neutral-600 shrink-0" />
                  <div className="flex items-center gap-2.5 flex-1">
                    <div className="w-5 h-5 rounded-full bg-lime-400 flex items-center justify-center">
                      <Check size={12} className="text-black" />
                    </div>
                    <span className="text-white text-sm font-medium">{item.after}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="relative">
            <div className="relative aspect-square max-w-md mx-auto">
              {/* Chaos elements */}
              <div className="absolute top-0 left-0 w-44 bg-neutral-900/80 border border-neutral-700 rounded-xl p-3 transform -rotate-6">
                <div className="grid grid-cols-4 gap-1 mb-2">
                  {[...Array(12)].map((_, i) => (
                    <div key={i} className="h-2.5 bg-neutral-700 rounded" />
                  ))}
                </div>
                <div className="text-xs text-neutral-500">deals_final_v23.xlsx</div>
              </div>
              
              <div className="absolute top-16 right-4 w-24 h-24 bg-amber-400 rounded-lg p-2.5 transform rotate-6 shadow-lg">
                <div className="h-1.5 bg-amber-600/40 rounded mb-1 w-3/4" />
                <div className="h-1.5 bg-amber-600/40 rounded mb-1 w-1/2" />
                <div className="text-xs text-amber-800 mt-2 font-medium">FOLLOW UP!!</div>
              </div>
              
              <div className="absolute bottom-24 left-4 w-32 bg-neutral-900/60 border border-neutral-700 rounded-xl p-3 transform rotate-3">
                <div className="text-xs text-neutral-500 mb-1.5">Re: Re: Re: Deal</div>
                <div className="h-1.5 bg-neutral-700 rounded mb-1" />
                <div className="h-1.5 bg-neutral-700 rounded w-2/3" />
              </div>
              
              {/* Solution card */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 bg-neutral-950 border-2 border-lime-400 rounded-2xl p-5 z-10 shadow-xl shadow-lime-400/10">
                <div className="flex items-center gap-2.5 mb-4">
                  <Logo size={24} />
                  <span className="text-white font-semibold text-sm">Sponsy</span>
                  <span className="ml-auto px-2 py-0.5 rounded-full text-xs font-bold bg-lime-400/20 text-lime-400">PRO</span>
                </div>
                <div className="space-y-2">
                  {[
                    { brand: 'Razer', letter: 'R', color: 'bg-green-500', stage: 'Contracted', amount: '$2,500', highlight: true },
                    { brand: 'Corsair', letter: 'C', color: 'bg-blue-500', stage: 'Negotiating', amount: '$4,200' },
                    { brand: 'GFuel', letter: 'G', color: 'bg-orange-500', stage: 'Lead', amount: '$1,800' },
                  ].map((deal, i) => (
                    <div key={i} className={`flex items-center justify-between p-2.5 rounded-lg ${deal.highlight ? 'bg-lime-400/10' : 'bg-white/5'}`}>
                      <div className="flex items-center gap-2.5">
                        <div className={`w-7 h-7 rounded-md ${deal.color} flex items-center justify-center text-xs font-bold text-white`}>
                          {deal.letter}
                        </div>
                        <div>
                          <div className="text-xs text-white font-medium">{deal.brand}</div>
                          <div className="text-xs text-neutral-500">{deal.stage}</div>
                        </div>
                      </div>
                      <div className={`text-sm font-bold ${deal.highlight ? 'text-lime-400' : 'text-white'}`}>
                        {deal.amount}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-neutral-800 flex items-center justify-between">
                  <span className="text-neutral-500 text-xs">Pipeline Total</span>
                  <span className="text-white font-bold">$8,500</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============ FEATURES ============
function Features() {
  const features = [
    { IconComp: Layout, title: 'Visual Deal Pipeline', description: 'Track every sponsorship from lead to payment with our Kanban board. Drag, drop, and never lose a deal.', color: 'text-lime-300', bg: 'bg-lime-400/30' },
    { IconComp: BarChart, title: 'Rate Benchmarking', description: 'See what creators with similar audiences charge. Never undersell yourself again.', color: 'text-blue-300', bg: 'bg-blue-400/30' },
    { IconComp: Building, title: 'Brand CRM', description: 'Store every contact, conversation, and deal history. Auto-fetch logos. Build real relationships.', color: 'text-purple-300', bg: 'bg-purple-400/30' },
    { IconComp: FileText, title: 'Professional Invoicing', description: 'Generate beautiful invoices in seconds. Track when they\'re viewed. Get paid faster.', color: 'text-amber-300', bg: 'bg-amber-400/30' },
    { IconComp: Sparkles, title: 'Media Kit Builder', description: 'Create a stunning, shareable media kit with your stats and rates. Impress brands instantly.', color: 'text-pink-300', bg: 'bg-pink-400/30' },
    { IconComp: Globe, title: 'Opportunity Marketplace', description: 'Browse brand opportunities and receive private invitations matched to your audience.', color: 'text-emerald-300', bg: 'bg-emerald-400/30' },
  ];

  return (
    <section id="features" className="py-20 md:py-28 bg-neutral-950">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex px-4 py-1.5 rounded-full text-sm font-medium mb-6 bg-lime-400/20 text-lime-300">
            Features
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Everything you need.<br />
            <span className="text-neutral-500">Nothing you don't.</span>
          </h2>
          <p className="text-lg text-neutral-400 max-w-2xl mx-auto">
            Built specifically for creators. No bloated enterprise features — just the tools you need to grow.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((feature, i) => (
            <div key={i} className="group p-7 rounded-2xl bg-neutral-900/50 border border-neutral-800 hover:border-neutral-700 transition-all duration-300 hover:-translate-y-1">
              <div className={`w-12 h-12 rounded-xl ${feature.bg} flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110`}>
                <feature.IconComp size={24} className={feature.color} />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
              <p className="text-neutral-400 text-sm leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============ HOW IT WORKS ============
function HowItWorks() {
  const steps = [
    { title: 'Add Your Brands', description: 'Import contacts or add new ones. We auto-fetch logos and organize everything.', IconComp: Building },
    { title: 'Track Your Deals', description: 'Move sponsorships through your pipeline. Never miss a follow-up.', IconComp: Layout },
    { title: 'Send Invoices', description: 'Generate professional invoices in one click. Track when they\'re viewed.', IconComp: FileText },
    { title: 'Get Paid', description: 'Watch payments roll in. Analyze earnings. Celebrate wins. Repeat.', IconComp: DollarSign },
  ];

  return (
    <section id="how-it-works" className="py-20 md:py-28 bg-black">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex px-4 py-1.5 rounded-full text-sm font-medium mb-6 bg-lime-400/20 text-lime-300">
            How It Works
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            From first contact to payday
          </h2>
          <p className="text-lg text-neutral-400 max-w-xl mx-auto">
            Four simple steps to transform your sponsorship chaos into a system.
          </p>
        </div>

        <div className="grid md:grid-cols-4 gap-6 md:gap-4">
          {steps.map((step, i) => (
            <div key={i} className="relative text-center md:text-left">
              {i < steps.length - 1 && (
                <div className="hidden md:block absolute top-10 left-[calc(50%+40px)] w-[calc(100%-80px)] h-px bg-gradient-to-r from-neutral-700 to-transparent" />
              )}
              
              <div className="relative inline-flex mb-5">
                <div className="w-20 h-20 rounded-2xl bg-neutral-800 border border-neutral-700 flex items-center justify-center">
                  <step.IconComp size={32} className="text-neutral-300" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-lime-400 flex items-center justify-center text-black text-sm font-bold shadow-lg shadow-lime-400/30">
                  {i + 1}
                </div>
              </div>
              
              <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
              <p className="text-neutral-400 text-sm leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============ COMPARISON ============
function Comparison() {
  const rows = [
    { feature: 'Commission on deals', us: '0%', them: '10-20%' },
    { feature: 'Visual deal pipeline', us: true, them: false },
    { feature: 'Rate benchmarking', us: true, them: false },
    { feature: 'Built-in invoicing', us: true, them: 'Extra cost' },
    { feature: 'Media kit builder', us: true, them: 'Basic' },
    { feature: 'Brand CRM', us: true, them: 'Limited' },
    { feature: 'Opportunity marketplace', us: true, them: true },
  ];

  return (
    <section className="py-20 md:py-28 bg-neutral-950">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="inline-flex px-4 py-1.5 rounded-full text-sm font-medium mb-6 bg-lime-400/20 text-lime-300">
            Why Sponsy
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Built different.
          </h2>
          <p className="text-neutral-400">
            See how we compare to other creator platforms.
          </p>
        </div>

        <div className="rounded-2xl border border-neutral-700 overflow-hidden">
          <div className="grid grid-cols-3 bg-neutral-800 p-4 border-b border-neutral-700">
            <div className="text-neutral-300 text-sm font-medium">Feature</div>
            <div className="text-center font-bold text-white">Sponsy</div>
            <div className="text-center text-neutral-400 text-sm">Others</div>
          </div>
          
          {rows.map((row, i) => (
            <div key={i} className={`grid grid-cols-3 p-4 items-center ${i % 2 === 0 ? 'bg-neutral-900/50' : 'bg-neutral-900/20'} ${i < rows.length - 1 ? 'border-b border-neutral-800' : ''}`}>
              <div className="text-neutral-200 text-sm">{row.feature}</div>
              <div className="flex justify-center">
                {row.us === true ? (
                  <div className="w-6 h-6 rounded-full bg-lime-400 flex items-center justify-center">
                    <Check size={14} className="text-black" />
                  </div>
                ) : (
                  <span className="font-bold text-lime-400 text-lg">{row.us}</span>
                )}
              </div>
              <div className="flex justify-center">
                {row.them === true ? (
                  <div className="w-6 h-6 rounded-full bg-neutral-600 flex items-center justify-center">
                    <Check size={14} className="text-neutral-300" />
                  </div>
                ) : row.them === false ? (
                  <div className="w-6 h-6 rounded-full bg-red-900/50 flex items-center justify-center">
                    <X size={14} className="text-red-400" />
                  </div>
                ) : (
                  <span className="text-neutral-400 text-sm">{row.them}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============ TESTIMONIALS ============
function Testimonials() {
  const testimonials = [
    { quote: "Sponsy showed me I was leaving $40K on the table every year. The rate benchmarks paid for themselves in my first week.", author: "Jake Williams", role: "Gaming Creator", stats: "1.2M subscribers", avatar: "JW" },
    { quote: "I used to dread sponsorship season. Now I actually enjoy it. Everything's organized and I get paid on time.", author: "Maria Santos", role: "Lifestyle Creator", stats: "450K followers", avatar: "MS" },
    { quote: "The invoicing feature changed my life. I went from chasing payments for 3+ months to getting paid in 2 weeks.", author: "Alex Chen", role: "Tech Reviewer", stats: "780K subscribers", avatar: "AC" },
    { quote: "Finally, a tool that gets creators. No bloated features, no learning curve — just exactly what I need.", author: "Emma Rodriguez", role: "Fitness Creator", stats: "320K followers", avatar: "ER" },
  ];

  return (
    <section id="testimonials" className="py-20 md:py-28 bg-black">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <div className="inline-flex px-4 py-1.5 rounded-full text-sm font-medium mb-6 bg-lime-400/20 text-lime-300">
            Testimonials
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
            Loved by creators
          </h2>
          <p className="text-neutral-400 text-lg">
            Join thousands who've transformed their workflow.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {testimonials.map((t, i) => (
            <div key={i} className="p-6 md:p-8 rounded-2xl bg-neutral-900/50 border border-neutral-800">
              <div className="flex gap-1 mb-5">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} size={16} fill="#BFFF00" stroke="#BFFF00" />
                ))}
              </div>
              
              <blockquote className="text-neutral-200 mb-6 leading-relaxed">
                "{t.quote}"
              </blockquote>
              
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-lime-400 flex items-center justify-center text-black text-sm font-bold">
                  {t.avatar}
                </div>
                <div>
                  <div className="font-semibold text-white text-sm">{t.author}</div>
                  <div className="text-neutral-500 text-xs">{t.role} • {t.stats}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============ PRICING ============
function Pricing() {
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  
  // Pricing aligned with Supabase tier_configs table
  const plans = [
    {
      name: 'Free',
      description: 'Try Sponsy risk-free',
      price: 0,
      yearlyPrice: 0,
      deals: 3,
      features: ['3 active deals', 'Basic pipeline view', 'Invoice tracking', 'Media kit builder', 'Email support'],
      cta: 'Get Started',
      popular: false,
    },
    {
      name: 'Starter',
      description: 'For growing creators',
      price: 15,
      yearlyPrice: 150,
      deals: 10,
      features: ['10 active deals', 'E-signature contracts', 'Basic analytics', 'Brand CRM', 'Priority email support'],
      cta: 'Start Free Trial',
      popular: false,
    },
    {
      name: 'Pro',
      description: 'For full-time creators',
      price: 40,
      yearlyPrice: 400,
      deals: 30,
      features: ['30 active deals', 'Advanced analytics', 'Custom fields', 'Export data', 'Priority support'],
      cta: 'Start Free Trial',
      popular: true,
    },
    {
      name: 'Business',
      description: 'For agencies & teams',
      price: 100,
      yearlyPrice: 1000,
      deals: 100,
      features: ['100 active deals', 'Everything in Pro', 'Team collaboration', 'API access'],
      cta: 'Start Free Trial',
      popular: false,
    },
    {
      name: 'Enterprise',
      description: 'For large creator networks',
      price: 200,
      yearlyPrice: 2000,
      deals: -1, // unlimited
      features: ['Unlimited deals', 'Everything in Business', 'Custom integrations', 'Dedicated support'],
      cta: 'Contact Sales',
      popular: false,
    },
  ];

  const getDisplayPrice = (plan) => {
    if (billingPeriod === 'yearly') {
      return Math.round(plan.yearlyPrice / 12);
    }
    return plan.price;
  };

  const getSavings = (plan) => {
    if (plan.price === 0) return null;
    const monthlyTotal = plan.price * 12;
    const savings = monthlyTotal - plan.yearlyPrice;
    if (savings > 0) {
      return `Save $${savings}/year`;
    }
    return null;
  };

  return (
    <section id="pricing" className="py-20 md:py-28 bg-neutral-950">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-10">
          <div className="inline-flex px-4 py-1.5 rounded-full text-sm font-medium mb-6 bg-lime-400/20 text-lime-300">
            Pricing
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
            Pay for what you use
          </h2>
          <p className="text-neutral-400 text-lg mb-2">
            No commissions. No hidden fees. Keep 100% of your earnings.
          </p>
          <p className="text-neutral-500 text-sm mb-8">
            All paid plans include 14-day free trial. Cancel anytime.
          </p>
          
          {/* Billing toggle */}
          <div className="inline-flex items-center gap-3 p-1.5 bg-neutral-900 rounded-full border border-neutral-800">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${billingPeriod === 'monthly' ? 'bg-lime-400 text-black' : 'text-neutral-400 hover:text-white'}`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${billingPeriod === 'yearly' ? 'bg-lime-400 text-black' : 'text-neutral-400 hover:text-white'}`}
            >
              Yearly
              <span className={`text-xs px-2 py-0.5 rounded-full ${billingPeriod === 'yearly' ? 'bg-black/20 text-black' : 'bg-lime-400/20 text-lime-400'}`}>
                2 months free
              </span>
            </button>
          </div>
        </div>

        {/* Pricing cards - horizontal scroll on mobile */}
        <div className="overflow-x-auto pb-4 -mx-6 px-6 pt-4">
          <div className="flex gap-4 min-w-max lg:min-w-0 lg:grid lg:grid-cols-5">
            {plans.map((plan, i) => (
              <div
                key={i}
                className={`w-64 lg:w-auto rounded-2xl p-5 relative flex flex-col ${plan.popular ? 'bg-lime-400/5 border-2 border-lime-400 mt-0' : 'bg-neutral-900/50 border border-neutral-800 mt-4'}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-lime-400 text-black text-xs font-bold rounded-full whitespace-nowrap z-10">
                    Most Popular
                  </div>
                )}
                
                <div className="mb-4">
                  <h3 className="text-lg font-bold text-white mb-0.5">{plan.name}</h3>
                  <p className="text-neutral-500 text-xs">{plan.description}</p>
                </div>
                
                <div className="mb-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-white">${getDisplayPrice(plan)}</span>
                    <span className="text-neutral-500 text-sm">/mo</span>
                  </div>
                  {billingPeriod === 'yearly' && getSavings(plan) && (
                    <span className="text-xs text-lime-400 font-medium">{getSavings(plan)}</span>
                  )}
                </div>
                
                <div className="mb-4 pb-4 border-b border-neutral-800">
                  <div className="flex items-center gap-2">
                    <span className={`text-2xl font-bold ${plan.popular ? 'text-lime-400' : 'text-white'}`}>
                      {plan.deals === -1 ? '∞' : plan.deals}
                    </span>
                    <span className="text-neutral-400 text-sm">active deals</span>
                  </div>
                </div>
                
                <ul className="space-y-2 mb-6 flex-1">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <div className="w-4 h-4 rounded-full bg-lime-400/20 flex items-center justify-center shrink-0 mt-0.5">
                        <Check size={10} className="text-lime-400" />
                      </div>
                      <span className="text-neutral-300 text-xs leading-relaxed">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <a
                  href={plan.name === 'Enterprise' ? 'mailto:sales@sponsy.app?subject=Sponsy Enterprise Inquiry' : `${APP_URL}?plan=${plan.name.toLowerCase()}&billing=${billingPeriod}`}
                  className={`block w-full py-3 rounded-full font-semibold text-sm transition-all text-center ${plan.popular ? 'bg-lime-400 text-black hover:bg-lime-300 shadow-lg shadow-lime-400/20' : 'border border-neutral-700 text-white hover:bg-white/5'}`}
                >
                  {plan.cta}
                </a>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10 mt-10">
          {[
            { IconComp: Shield, text: '30-day money-back guarantee' },
            { IconComp: Lock, text: 'Bank-level encryption' },
            { IconComp: Headphones, text: '24/7 creator support' },
          ].map((badge, i) => (
            <div key={i} className="flex items-center gap-2 text-neutral-400">
              <badge.IconComp size={18} />
              <span className="text-sm">{badge.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============ FAQ ============
function FAQ() {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    { q: "Is Sponsy really free to start?", a: "Yes! Our Free plan lets you manage up to 3 active deals with no time limit and no credit card required. It's perfect for trying Sponsy or managing occasional sponsorships." },
    { q: "What counts as an 'active deal'?", a: "An active deal is any sponsorship that's in progress — from initial lead to invoiced. Once a deal is marked as Paid, Completed, or Lost, it no longer counts toward your limit. This means you can close hundreds of deals over time on any plan." },
    { q: "Do you take a commission on my deals?", a: "Never. Unlike other platforms that take 10-20% of your earnings, Sponsy charges a flat monthly fee based on how many deals you're managing. You keep 100% of what you earn from brands." },
    { q: "How does pricing scale with my business?", a: "Our tiers are based on active deals: Free (3), Starter (10) at $15/mo, Pro (30) at $40/mo, Business (100) at $100/mo, and Enterprise (unlimited) at $200/mo. As your business grows, simply upgrade. Pay yearly and save 2 months!" },
    { q: "Can I import existing contacts?", a: "Absolutely! Import via CSV or JSON backup. Our team helps with migration for free on Pro plans and above." },
    { q: "Is my data secure?", a: "Yes. We use bank-level 256-bit encryption and secure cloud hosting. Your deal information and financial data are completely private and never shared." },
    { q: "Can I cancel anytime?", a: "Yes. No contracts, no commitments. Cancel from your account settings anytime. We also offer a 30-day money-back guarantee on all paid plans." },
  ];

  return (
    <section className="py-20 md:py-28 bg-black">
      <div className="max-w-2xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="inline-flex px-4 py-1.5 rounded-full text-sm font-medium mb-6 bg-lime-400/20 text-lime-300">
            FAQ
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white">
            Questions? Answers.
          </h2>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div key={i} className="rounded-xl border border-neutral-800 overflow-hidden bg-neutral-900/30">
              <button 
                onClick={() => setOpenIndex(openIndex === i ? -1 : i)} 
                className="w-full px-5 py-4 flex items-center justify-between text-left"
              >
                <span className="font-medium text-white pr-4 text-sm md:text-base">{faq.q}</span>
                <ChevronDown size={18} className={`text-neutral-400 shrink-0 transition-transform duration-200 ${openIndex === i ? 'rotate-180' : ''}`} />
              </button>
              {openIndex === i && (
                <div className="px-5 pb-4">
                  <p className="text-neutral-400 text-sm leading-relaxed">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============ FINAL CTA ============
function FinalCTA() {
  return (
    <section className="py-20 md:py-28 bg-neutral-950 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-t from-lime-400/5 to-transparent" />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-lime-400/10 rounded-full blur-3xl" />
      
      <div className="relative max-w-3xl mx-auto px-6 text-center">
        <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
          Ready to take control of<br />
          <span className="text-lime-400">your creator business?</span>
        </h2>
        <p className="text-lg text-neutral-300 mb-10 max-w-xl mx-auto">
          Join 2,847 creators who've made the switch. Start free — no credit card required.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a href={`${APP_URL}?signup=true`} className="group w-full sm:w-auto px-8 py-4 bg-lime-400 text-black font-bold rounded-full text-base hover:bg-lime-300 transition-all flex items-center justify-center gap-2 shadow-lg shadow-lime-400/25">
            Get Started Free
            <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
          </a>
          <a href="mailto:support@sponsy.app?subject=Sponsy Demo Request" className="w-full sm:w-auto px-8 py-4 border border-neutral-700 text-white font-semibold rounded-full text-base hover:bg-white/5 transition-all text-center">
            Schedule a Demo
          </a>
        </div>
      </div>
    </section>
  );
}

// ============ PRIVACY POLICY PAGE ============
function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-black text-white py-20 px-6">
      <div className="max-w-3xl mx-auto">
        <a href="/" className="inline-flex items-center gap-2 text-lime-400 hover:text-lime-300 transition-colors mb-8">
          <ArrowRight size={16} className="rotate-180" />
          Back to Home
        </a>
        
        <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
        <p className="text-neutral-400 mb-8">Last updated: December 25, 2025</p>
        
        <div className="prose prose-invert prose-neutral max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">1. Introduction</h2>
            <p className="text-neutral-300 leading-relaxed">
              Welcome to Sponsy ("we," "our," or "us"), a Florida-based company. We are committed to protecting
              your privacy and ensuring the security of your personal information in compliance with the Florida
              Digital Bill of Rights (FDBR), the Federal Trade Commission Act, and other applicable laws. This
              Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use
              our creator sponsorship CRM platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">2. Information We Collect</h2>
            <h3 className="text-xl font-medium text-white mb-2">Personal Information</h3>
            <p className="text-neutral-300 leading-relaxed mb-4">When you create an account, we collect:</p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2">
              <li>Email address (required for account creation)</li>
              <li>Name (optional)</li>
              <li>Profile information you choose to provide</li>
              <li>Payment information (processed securely by Stripe — we do not store credit card numbers)</li>
            </ul>
            
            <h3 className="text-xl font-medium text-white mb-2 mt-6">Business Information</h3>
            <p className="text-neutral-300 leading-relaxed mb-4">To provide our services, we store:</p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2">
              <li>Brand and contact information you enter</li>
              <li>Deal and sponsorship details</li>
              <li>Invoice and contract information</li>
              <li>Media kit content</li>
            </ul>
            
            <h3 className="text-xl font-medium text-white mb-2 mt-6">Automatically Collected Information</h3>
            <ul className="list-disc list-inside text-neutral-300 space-y-2">
              <li>Device and browser information</li>
              <li>IP address and approximate location data</li>
              <li>Usage patterns and feature interactions</li>
              <li>Cookies and similar technologies (see Section 7)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">3. Legal Basis for Processing (Florida Residents)</h2>
            <p className="text-neutral-300 leading-relaxed mb-4">
              Under the Florida Digital Bill of Rights and applicable federal law, we process your data based on:
            </p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2">
              <li><strong>Consent:</strong> When you create an account and agree to these terms</li>
              <li><strong>Contract Performance:</strong> To provide the services you've requested</li>
              <li><strong>Legitimate Interests:</strong> To improve our services and prevent fraud</li>
              <li><strong>Legal Compliance:</strong> To meet tax, accounting, and legal requirements</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">4. How We Use Your Information</h2>
            <p className="text-neutral-300 leading-relaxed mb-4">We use your information to:</p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2">
              <li>Provide and maintain the Sponsy platform</li>
              <li>Process transactions and send related information</li>
              <li>Send administrative messages, updates, and security alerts</li>
              <li>Respond to your comments, questions, and support requests</li>
              <li>Analyze usage to improve our services (aggregated, anonymized data)</li>
              <li>Detect and prevent fraud, abuse, or unauthorized access</li>
              <li>Comply with legal obligations under Florida and federal law</li>
            </ul>
            <p className="text-neutral-300 leading-relaxed mt-4">
              <strong>We do not:</strong> Sell your personal information, use it for targeted advertising from
              third parties, or share it with data brokers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">5. Information Sharing</h2>
            <p className="text-neutral-300 leading-relaxed mb-4">
              We do not sell, rent, or trade your personal information. We may share information only with:
            </p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2">
              <li><strong>Service Providers:</strong> Third parties that help us operate (Supabase for database hosting, Stripe for payment processing, DocuSign for e-signatures). These providers are contractually bound to protect your data.</li>
              <li><strong>Legal Requirements:</strong> When required by Florida or federal law, court order, or government request</li>
              <li><strong>Business Transfers:</strong> In connection with merger, acquisition, or sale of assets (you will be notified)</li>
              <li><strong>With Your Explicit Consent:</strong> For any other purpose you specifically approve</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">6. Data Security</h2>
            <p className="text-neutral-300 leading-relaxed">
              We implement industry-standard security measures to protect your information:
            </p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2 mt-4">
              <li>256-bit SSL/TLS encryption for all data in transit</li>
              <li>AES-256 encryption for data at rest</li>
              <li>Regular security assessments and penetration testing</li>
              <li>Multi-factor authentication options</li>
              <li>Access controls and employee training</li>
              <li>Secure cloud infrastructure (Vercel, Supabase) with SOC 2 compliance</li>
            </ul>
            <p className="text-neutral-300 leading-relaxed mt-4">
              In the event of a data breach affecting your personal information, we will notify you within
              30 days as required by Florida law (F.S. § 501.171).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">7. Your Rights (Florida Digital Bill of Rights)</h2>
            <p className="text-neutral-300 leading-relaxed mb-4">
              As a Florida resident, you have the following rights under the Florida Digital Bill of Rights:
            </p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2">
              <li><strong>Right to Know:</strong> Request what personal data we collect and how it's used</li>
              <li><strong>Right to Access:</strong> Obtain a copy of your personal data</li>
              <li><strong>Right to Correction:</strong> Request correction of inaccurate data</li>
              <li><strong>Right to Deletion:</strong> Request deletion of your personal data</li>
              <li><strong>Right to Portability:</strong> Export your data in a machine-readable format</li>
              <li><strong>Right to Opt-Out:</strong> Opt out of data processing for targeted advertising or sale of personal data (we don't do either)</li>
            </ul>
            <p className="text-neutral-300 leading-relaxed mt-4">
              To exercise these rights, contact us at <strong>support@sponsy.app</strong>. We will respond
              within 45 days as required by law. You may also appeal any denied request.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">8. Cookies and Tracking Technologies</h2>
            <p className="text-neutral-300 leading-relaxed mb-4">We use the following types of cookies:</p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2">
              <li><strong>Essential Cookies:</strong> Required for authentication and security (cannot be disabled)</li>
              <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
              <li><strong>Analytics Cookies:</strong> Help us understand usage patterns (anonymized)</li>
            </ul>
            <p className="text-neutral-300 leading-relaxed mt-4">
              We do not use advertising or tracking cookies from third parties. You can control non-essential
              cookies through your browser settings.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">9. Third-Party Services</h2>
            <p className="text-neutral-300 leading-relaxed mb-4">Our service integrates with:</p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2">
              <li><strong>Google OAuth:</strong> For authentication (<a href="https://policies.google.com/privacy" className="text-lime-400 hover:underline" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a>)</li>
              <li><strong>Stripe:</strong> For payment processing (<a href="https://stripe.com/privacy" className="text-lime-400 hover:underline" target="_blank" rel="noopener noreferrer">Stripe Privacy Policy</a>)</li>
              <li><strong>DocuSign:</strong> For e-signatures (<a href="https://www.docusign.com/privacy" className="text-lime-400 hover:underline" target="_blank" rel="noopener noreferrer">DocuSign Privacy Policy</a>)</li>
              <li><strong>Supabase:</strong> For data storage (<a href="https://supabase.com/privacy" className="text-lime-400 hover:underline" target="_blank" rel="noopener noreferrer">Supabase Privacy Policy</a>)</li>
            </ul>
            <p className="text-neutral-300 leading-relaxed mt-4">
              Each provider has their own privacy policy. We recommend reviewing them to understand how
              they handle your data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">10. Data Retention</h2>
            <p className="text-neutral-300 leading-relaxed">
              We retain your data as long as your account is active. After account deletion:
            </p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2 mt-4">
              <li>Personal data is deleted within 30 days</li>
              <li>Backup copies are purged within 90 days</li>
              <li>Financial records may be retained for 7 years as required by IRS regulations</li>
              <li>Data related to legal disputes may be retained until resolution</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">11. Children's Privacy</h2>
            <p className="text-neutral-300 leading-relaxed">
              Sponsy is not intended for users under 18 years of age. We do not knowingly collect personal
              information from children under 13 in compliance with the Children's Online Privacy Protection
              Act (COPPA). If you believe we have collected information from a child under 13, please contact
              us immediately at <strong>support@sponsy.app</strong> and we will promptly delete it.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">12. International Data Transfers</h2>
            <p className="text-neutral-300 leading-relaxed">
              Your information may be transferred to and processed on servers located outside of Florida
              or the United States. Our service providers (Supabase, Vercel) maintain servers in multiple
              regions. We ensure appropriate safeguards through contractual obligations and industry-standard
              security practices.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">13. Do Not Track Signals</h2>
            <p className="text-neutral-300 leading-relaxed">
              We honor Do Not Track (DNT) browser signals. When DNT is enabled, we limit data collection
              to essential functionality only.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">14. Changes to This Policy</h2>
            <p className="text-neutral-300 leading-relaxed">
              We may update this Privacy Policy to reflect changes in our practices or applicable law.
              For material changes, we will notify you by email at least 30 days before the changes take
              effect. The "Last updated" date at the top of this policy indicates when it was last revised.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">15. Contact Us</h2>
            <p className="text-neutral-300 leading-relaxed">
              If you have questions about this Privacy Policy, want to exercise your rights, or have
              concerns about our data practices, contact us at:
            </p>
            <p className="text-neutral-300 mt-4">
              <strong>Sponsy</strong><br />
              990 N SR 434<br />
              Altamonte Springs, FL 32714<br />
              United States<br /><br />
              <strong>Legal Inquiries:</strong> info@sponsy.app<br />
              <strong>Customer Support & Data Requests:</strong> support@sponsy.app<br />
              <strong>Website:</strong> www.sponsy.app
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

// ============ TERMS OF SERVICE PAGE ============
function TermsOfService() {
  return (
    <div className="min-h-screen bg-black text-white py-20 px-6">
      <div className="max-w-3xl mx-auto">
        <a href="/" className="inline-flex items-center gap-2 text-lime-400 hover:text-lime-300 transition-colors mb-8">
          <ArrowRight size={16} className="rotate-180" />
          Back to Home
        </a>
        
        <h1 className="text-4xl font-bold mb-4">Terms of Service</h1>
        <p className="text-neutral-400 mb-8">Last updated: December 25, 2025</p>
        
        <div className="prose prose-invert prose-neutral max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">1. Agreement to Terms</h2>
            <p className="text-neutral-300 leading-relaxed">
              By accessing or using Sponsy ("the Service"), you agree to be bound by these Terms of Service
              ("Terms"). If you disagree with any part of these terms, you do not have permission to access
              the Service. These Terms apply to all visitors, users, and others who access or use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">2. Description of Service</h2>
            <p className="text-neutral-300 leading-relaxed">
              Sponsy is a Customer Relationship Management (CRM) platform designed for content creators to
              manage their sponsorship deals, brand relationships, invoices, and contracts. The Service includes
              features such as deal pipeline management, invoicing, contract creation and e-signatures,
              analytics, and media kit building.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">3. User Accounts</h2>
            <p className="text-neutral-300 leading-relaxed mb-4">
              When you create an account with us, you must provide accurate, complete, and current information.
              Failure to do so constitutes a breach of the Terms, which may result in immediate termination of
              your account.
            </p>
            <p className="text-neutral-300 leading-relaxed">
              You are responsible for safeguarding the password that you use to access the Service and for any
              activities or actions under your password. You agree not to disclose your password to any third party.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">4. Subscription and Payments</h2>
            <h3 className="text-xl font-medium text-white mb-2">Billing</h3>
            <p className="text-neutral-300 leading-relaxed mb-4">
              Some parts of the Service are billed on a subscription basis ("Subscription(s)"). You will be
              billed in advance on a recurring and periodic basis ("Billing Cycle"). Billing cycles are set
              on a monthly or annual basis, depending on the type of subscription plan you select.
            </p>
            
            <h3 className="text-xl font-medium text-white mb-2">Free Trial</h3>
            <p className="text-neutral-300 leading-relaxed mb-4">
              Sponsy may offer a free trial period. At any time and without notice, Sponsy reserves the right
              to modify the terms and conditions of the free trial offer, or cancel it.
            </p>

            <h3 className="text-xl font-medium text-white mb-2">Refunds</h3>
            <p className="text-neutral-300 leading-relaxed">
              We offer a 30-day money-back guarantee on all paid subscription plans. If you are not satisfied
              with the Service within 30 days of your initial purchase, contact us for a full refund.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">5. Acceptable Use</h2>
            <p className="text-neutral-300 leading-relaxed mb-4">You agree not to use the Service to:</p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2">
              <li>Violate any laws or regulations</li>
              <li>Infringe upon the rights of others</li>
              <li>Send spam, fraudulent, or deceptive content</li>
              <li>Distribute malware or harmful code</li>
              <li>Attempt to gain unauthorized access to the Service</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Collect user information without consent</li>
              <li>Use the Service for any illegal or unauthorized purpose</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">6. Intellectual Property</h2>
            <p className="text-neutral-300 leading-relaxed mb-4">
              The Service and its original content (excluding content provided by users), features, and
              functionality are and will remain the exclusive property of Sponsy and its licensors. The Service
              is protected by copyright, trademark, and other laws.
            </p>
            <p className="text-neutral-300 leading-relaxed">
              You retain ownership of any content you create using the Service (deals, contracts, invoices,
              media kits, etc.). By using the Service, you grant us a limited license to store and process
              your content solely to provide the Service to you.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">7. Electronic Signatures</h2>
            <p className="text-neutral-300 leading-relaxed mb-4">
              The Service includes electronic signature functionality powered by DocuSign. By using this feature:
            </p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2">
              <li>You agree that electronic signatures are legally binding</li>
              <li>You consent to conducting business electronically</li>
              <li>You agree to DocuSign's terms of service</li>
              <li>You acknowledge that you are authorized to sign on behalf of yourself or your organization</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">8. Financial Information</h2>
            <p className="text-neutral-300 leading-relaxed">
              Sponsy is not a financial institution. While we provide tools to track payments and create invoices,
              we do not process payments directly (payments are processed by Stripe). We are not responsible for
              any tax obligations arising from your use of the Service. You are responsible for ensuring the
              accuracy of your financial records and complying with applicable tax laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">9. Limitation of Liability</h2>
            <p className="text-neutral-300 leading-relaxed">
              In no event shall Sponsy, nor its directors, employees, partners, agents, suppliers, or affiliates,
              be liable for any indirect, incidental, special, consequential, or punitive damages, including
              without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from:
            </p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2 mt-4">
              <li>Your access to or use of (or inability to access or use) the Service</li>
              <li>Any conduct or content of any third party on the Service</li>
              <li>Any content obtained from the Service</li>
              <li>Unauthorized access, use, or alteration of your transmissions or content</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">10. Disclaimer</h2>
            <p className="text-neutral-300 leading-relaxed">
              Your use of the Service is at your sole risk. The Service is provided on an "AS IS" and "AS AVAILABLE"
              basis. The Service is provided without warranties of any kind, whether express or implied, including,
              but not limited to, implied warranties of merchantability, fitness for a particular purpose,
              non-infringement, or course of performance.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">11. Termination</h2>
            <p className="text-neutral-300 leading-relaxed mb-4">
              We may terminate or suspend your account immediately, without prior notice or liability, for any
              reason whatsoever, including without limitation if you breach the Terms.
            </p>
            <p className="text-neutral-300 leading-relaxed">
              Upon termination, your right to use the Service will immediately cease. If you wish to terminate
              your account, you may simply discontinue using the Service or contact us to delete your account.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">12. Data Export</h2>
            <p className="text-neutral-300 leading-relaxed">
              You may export your data at any time through the Service's export functionality. Upon account
              termination, we will retain your data for 30 days, after which it will be permanently deleted
              unless required by law to retain it longer.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">13. Changes to Terms</h2>
            <p className="text-neutral-300 leading-relaxed">
              We reserve the right, at our sole discretion, to modify or replace these Terms at any time.
              If a revision is material, we will try to provide at least 30 days' notice prior to any new
              terms taking effect. What constitutes a material change will be determined at our sole discretion.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">14. Governing Law</h2>
            <p className="text-neutral-300 leading-relaxed">
              These Terms shall be governed and construed in accordance with the laws of the State of Florida,
              United States, without regard to its conflict of law provisions. Any disputes arising from these
              Terms shall be resolved in the state or federal courts located in Orange County, Florida. Our
              failure to enforce any right or provision of these Terms will not be considered a waiver of those
              rights. You agree to comply with all applicable local, state, and federal laws, including the
              Florida Digital Bill of Rights.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">15. Contact Us</h2>
            <p className="text-neutral-300 leading-relaxed">
              If you have any questions about these Terms, please contact us at:
            </p>
            <p className="text-neutral-300 mt-4">
              <strong>Sponsy</strong><br />
              990 N SR 434<br />
              Altamonte Springs, FL 32714<br />
              United States<br /><br />
              <strong>Legal Inquiries:</strong> info@sponsy.app<br />
              <strong>Customer Support:</strong> support@sponsy.app<br />
              <strong>Website:</strong> www.sponsy.app
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

// ============ PAGE LAYOUT WRAPPER ============
function PageLayout({ children, title, subtitle }) {
  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      <div className="pt-24 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <a href="/" className="inline-flex items-center gap-2 text-lime-400 hover:text-lime-300 transition-colors mb-8">
            <ArrowRight size={16} className="rotate-180" />
            Back to Home
          </a>
          {title && <h1 className="text-4xl font-bold mb-4">{title}</h1>}
          {subtitle && <p className="text-neutral-400 mb-8">{subtitle}</p>}
          {children}
        </div>
      </div>
      <Footer />
    </div>
  );
}

// ============ ABOUT PAGE ============
function AboutPage() {
  return (
    <PageLayout title="About Sponsy" subtitle="Empowering creators to build sustainable businesses">
      <div className="prose prose-invert max-w-none space-y-8">
        <section>
          <h2 className="text-2xl font-semibold text-white mb-4">Our Mission</h2>
          <p className="text-neutral-300 leading-relaxed">
            Sponsy was founded with a simple mission: give content creators the professional tools they need
            to manage sponsorships without the complexity of enterprise software or the overhead of agencies
            taking 10-20% of their earnings.
          </p>
          <p className="text-neutral-300 leading-relaxed mt-4">
            We believe every creator deserves to keep 100% of what they earn while having access to the same
            caliber of deal management tools used by major media companies.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-4">Our Story</h2>
          <p className="text-neutral-300 leading-relaxed">
            As creators ourselves, we experienced firsthand the chaos of managing sponsorships through
            spreadsheets, email chains, and sticky notes. We built Sponsy to solve our own problem — and
            discovered thousands of other creators facing the same challenges.
          </p>
          <p className="text-neutral-300 leading-relaxed mt-4">
            Today, Sponsy helps thousands of creators worldwide manage millions of dollars in sponsorship deals,
            all while keeping their focus where it belongs: creating great content.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-4">Our Values</h2>
          <div className="grid md:grid-cols-2 gap-6 mt-6">
            {[
              { title: 'Creator-First', desc: 'Every feature is designed with creators in mind. No bloat, no enterprise complexity.' },
              { title: 'Transparency', desc: 'No hidden fees, no commissions. What you see is what you pay.' },
              { title: 'Privacy', desc: 'Your data is yours. We never sell your information or share it with advertisers.' },
              { title: 'Simplicity', desc: 'Powerful features that are actually easy to use. No training required.' },
            ].map((value, i) => (
              <div key={i} className="p-5 rounded-xl bg-neutral-900/50 border border-neutral-800">
                <h3 className="font-semibold text-white mb-2">{value.title}</h3>
                <p className="text-neutral-400 text-sm">{value.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-4">Contact</h2>
          <p className="text-neutral-300">
            <strong>Sponsy</strong><br />
            990 N SR 434<br />
            Altamonte Springs, FL 32714<br />
            United States<br /><br />
            <strong>Email:</strong> support@sponsy.app
          </p>
        </section>
      </div>
    </PageLayout>
  );
}

// ============ CAREERS PAGE ============
function CareersPage() {
  return (
    <PageLayout title="Careers" subtitle="Join us in empowering the creator economy">
      <div className="prose prose-invert max-w-none space-y-8">
        <section>
          <p className="text-neutral-300 leading-relaxed text-lg">
            We're a small, remote-first team passionate about building the best tools for content creators.
            We value autonomy, creativity, and a healthy work-life balance.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-6">Open Positions</h2>
          <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-8 text-center">
            <p className="text-neutral-400 mb-4">
              We don't have any open positions at the moment, but we're always interested in hearing
              from talented people.
            </p>
            <a
              href="mailto:support@sponsy.app?subject=Career Inquiry"
              className="inline-flex items-center gap-2 px-6 py-3 bg-lime-400 text-black font-semibold rounded-full hover:bg-lime-300 transition-colors"
            >
              Send us your resume
              <ArrowRight size={16} />
            </a>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-4">Benefits</h2>
          <ul className="list-disc list-inside text-neutral-300 space-y-2">
            <li>Fully remote - work from anywhere</li>
            <li>Competitive salary and equity</li>
            <li>Flexible hours</li>
            <li>Health insurance (US employees)</li>
            <li>Unlimited PTO</li>
            <li>Equipment budget</li>
            <li>Learning & development budget</li>
          </ul>
        </section>
      </div>
    </PageLayout>
  );
}

// ============ CONTACT PAGE ============
function ContactPage() {
  return (
    <PageLayout title="Contact Us" subtitle="We'd love to hear from you">
      <div className="prose prose-invert max-w-none space-y-8">
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h2 className="text-xl font-semibold text-white mb-4">Get in Touch</h2>
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-neutral-900/50 border border-neutral-800">
                <h3 className="font-medium text-white mb-1">General Inquiries</h3>
                <a href="mailto:support@sponsy.app" className="text-lime-400 hover:text-lime-300">support@sponsy.app</a>
              </div>
              <div className="p-4 rounded-xl bg-neutral-900/50 border border-neutral-800">
                <h3 className="font-medium text-white mb-1">Customer Support</h3>
                <a href="mailto:support@sponsy.app" className="text-lime-400 hover:text-lime-300">support@sponsy.app</a>
              </div>
              <div className="p-4 rounded-xl bg-neutral-900/50 border border-neutral-800">
                <h3 className="font-medium text-white mb-1">Press & Media</h3>
                <a href="mailto:support@sponsy.app" className="text-lime-400 hover:text-lime-300">support@sponsy.app</a>
              </div>
              <div className="p-4 rounded-xl bg-neutral-900/50 border border-neutral-800">
                <h3 className="font-medium text-white mb-1">Enterprise Sales</h3>
                <a href="mailto:support@sponsy.app" className="text-lime-400 hover:text-lime-300">support@sponsy.app</a>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-white mb-4">Office</h2>
            <div className="p-6 rounded-xl bg-neutral-900/50 border border-neutral-800">
              <p className="text-neutral-300">
                <strong className="text-white">Sponsy</strong><br />
                990 N SR 434<br />
                Altamonte Springs, FL 32714<br />
                United States
              </p>
            </div>

            <h2 className="text-xl font-semibold text-white mb-4 mt-8">Social</h2>
            <div className="flex gap-3">
              {[
                { name: 'Twitter', url: 'https://twitter.com/sponsyapp' },
                { name: 'Instagram', url: 'https://instagram.com/sponsyapp' },
                { name: 'YouTube', url: 'https://youtube.com/@sponsyapp' },
              ].map((social, i) => (
                <a
                  key={i}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-neutral-300 hover:text-white transition-colors text-sm"
                >
                  {social.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}

// ============ BLOG PAGE ============
function BlogPage() {
  const posts = [
    { title: 'How to Price Your Sponsorships in 2025', date: 'Dec 20, 2025', category: 'Guides', excerpt: 'Learn the strategies top creators use to price their brand deals and maximize earnings.' },
    { title: 'Announcing E-Signature Integration', date: 'Dec 15, 2025', category: 'Product', excerpt: 'Sign contracts digitally with our new DocuSign integration — no more printing and scanning.' },
    { title: '5 Mistakes Creators Make with Invoicing', date: 'Dec 10, 2025', category: 'Tips', excerpt: 'Avoid these common invoicing pitfalls that cost creators thousands each year.' },
  ];

  return (
    <PageLayout title="Blog" subtitle="Insights, updates, and guides for creators">
      <div className="space-y-6">
        {posts.map((post, i) => (
          <article key={i} className="p-6 rounded-xl bg-neutral-900/50 border border-neutral-800 hover:border-neutral-700 transition-colors">
            <div className="flex items-center gap-3 mb-3">
              <span className="px-2.5 py-0.5 rounded-full bg-lime-400/20 text-lime-400 text-xs font-medium">{post.category}</span>
              <span className="text-neutral-500 text-sm">{post.date}</span>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2 hover:text-lime-400 transition-colors cursor-pointer">{post.title}</h2>
            <p className="text-neutral-400 text-sm">{post.excerpt}</p>
          </article>
        ))}

        <div className="text-center pt-8">
          <p className="text-neutral-500">More posts coming soon!</p>
        </div>
      </div>
    </PageLayout>
  );
}

// ============ HELP CENTER PAGE ============
function HelpPage() {
  const categories = [
    { title: 'Getting Started', icon: '🚀', articles: ['Creating your account', 'Setting up your profile', 'Adding your first brand'] },
    { title: 'Deal Management', icon: '💼', articles: ['Using the pipeline', 'Moving deals between stages', 'Tracking deal values'] },
    { title: 'Invoicing', icon: '📄', articles: ['Creating invoices', 'Sending invoices to brands', 'Tracking payment status'] },
    { title: 'Contracts', icon: '📝', articles: ['Creating contracts', 'Using templates', 'E-signatures with DocuSign'] },
    { title: 'Billing', icon: '💳', articles: ['Subscription plans', 'Changing your plan', 'Canceling your subscription'] },
    { title: 'Account', icon: '⚙️', articles: ['Updating profile', 'Changing password', 'Deleting your account'] },
  ];

  return (
    <PageLayout title="Help Center" subtitle="Find answers to common questions">
      <div className="space-y-8">
        <div className="p-4 rounded-xl bg-neutral-900/50 border border-neutral-800">
          <input
            type="text"
            placeholder="Search for help..."
            className="w-full bg-transparent text-white placeholder-neutral-500 outline-none"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {categories.map((cat, i) => (
            <div key={i} className="p-5 rounded-xl bg-neutral-900/50 border border-neutral-800">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl">{cat.icon}</span>
                <h2 className="text-lg font-semibold text-white">{cat.title}</h2>
              </div>
              <ul className="space-y-2">
                {cat.articles.map((article, j) => (
                  <li key={j}>
                    <span className="text-neutral-400 hover:text-lime-400 transition-colors cursor-pointer text-sm">{article}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="p-6 rounded-xl bg-lime-400/10 border border-lime-400/20 text-center">
          <h3 className="text-lg font-semibold text-white mb-2">Can't find what you're looking for?</h3>
          <p className="text-neutral-400 text-sm mb-4">Our support team is here to help.</p>
          <a href="mailto:support@sponsy.app" className="inline-flex items-center gap-2 px-5 py-2.5 bg-lime-400 text-black font-semibold rounded-full hover:bg-lime-300 transition-colors">
            Contact Support
          </a>
        </div>
      </div>
    </PageLayout>
  );
}

// ============ CREATOR GUIDE PAGE ============
function GuidePage() {
  return (
    <PageLayout title="Creator Guide" subtitle="Everything you need to know about managing sponsorships">
      <div className="prose prose-invert max-w-none space-y-8">
        <section>
          <h2 className="text-2xl font-semibold text-white mb-4">Welcome to Sponsy</h2>
          <p className="text-neutral-300 leading-relaxed">
            This guide will walk you through everything you need to know about using Sponsy to manage
            your sponsorships like a pro. Whether you're just starting out or already closing six-figure
            deals, we've got you covered.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-4">Table of Contents</h2>
          <ol className="list-decimal list-inside text-neutral-300 space-y-2">
            <li className="hover:text-lime-400 cursor-pointer">Setting Up Your Account</li>
            <li className="hover:text-lime-400 cursor-pointer">Understanding the Deal Pipeline</li>
            <li className="hover:text-lime-400 cursor-pointer">Managing Brand Relationships</li>
            <li className="hover:text-lime-400 cursor-pointer">Creating Professional Invoices</li>
            <li className="hover:text-lime-400 cursor-pointer">Contract Templates & E-Signatures</li>
            <li className="hover:text-lime-400 cursor-pointer">Building Your Media Kit</li>
            <li className="hover:text-lime-400 cursor-pointer">Analyzing Your Earnings</li>
            <li className="hover:text-lime-400 cursor-pointer">Pricing Your Content</li>
          </ol>
        </section>

        <section className="p-6 rounded-xl bg-lime-400/10 border border-lime-400/20">
          <h3 className="text-lg font-semibold text-white mb-2">📚 Full guide coming soon!</h3>
          <p className="text-neutral-400 text-sm">
            We're working on comprehensive documentation. In the meantime, check out our
            <a href="/help" className="text-lime-400 hover:underline ml-1">Help Center</a> or
            <a href="mailto:support@sponsy.app" className="text-lime-400 hover:underline ml-1">contact support</a>.
          </p>
        </section>
      </div>
    </PageLayout>
  );
}

// ============ TEMPLATES PAGE ============
function TemplatesPage() {
  const templates = [
    { name: 'Sponsorship Agreement', category: 'Contracts', desc: 'Standard template for sponsored content deals' },
    { name: 'Brand Ambassador Contract', category: 'Contracts', desc: 'Long-term partnership agreement template' },
    { name: 'Professional Invoice', category: 'Invoices', desc: 'Clean, modern invoice template' },
    { name: 'YouTube Integration', category: 'Contracts', desc: 'Specific terms for YouTube sponsored videos' },
    { name: 'Podcast Ad Read', category: 'Contracts', desc: 'Template for podcast sponsorship segments' },
    { name: 'Social Media Package', category: 'Contracts', desc: 'Multi-platform content package template' },
  ];

  return (
    <PageLayout title="Templates" subtitle="Professional templates to help you close deals faster">
      <div className="space-y-8">
        <p className="text-neutral-300">
          All templates are available in Sponsy. Create an account to access and customize them for your brand.
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          {templates.map((template, i) => (
            <div key={i} className="p-5 rounded-xl bg-neutral-900/50 border border-neutral-800 hover:border-neutral-700 transition-colors">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-white">{template.name}</h3>
                <span className="px-2 py-0.5 rounded-full bg-neutral-800 text-neutral-400 text-xs">{template.category}</span>
              </div>
              <p className="text-neutral-400 text-sm">{template.desc}</p>
            </div>
          ))}
        </div>

        <div className="text-center pt-4">
          <a href={APP_URL + '?signup=true'} className="inline-flex items-center gap-2 px-6 py-3 bg-lime-400 text-black font-semibold rounded-full hover:bg-lime-300 transition-colors">
            Get Started Free
            <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </PageLayout>
  );
}

// ============ INTEGRATIONS PAGE ============
function IntegrationsPage() {
  const integrations = [
    { name: 'Google', desc: 'Sign in with your Google account for quick, secure authentication', status: 'Live', icon: '🔐' },
    { name: 'Stripe', desc: 'Accept payments and manage subscriptions securely', status: 'Live', icon: '💳' },
    { name: 'DocuSign', desc: 'Send contracts for legally binding e-signatures', status: 'Live', icon: '✍️' },
    { name: 'Zapier', desc: 'Connect Sponsy to 5,000+ apps and automate workflows', status: 'Coming Soon', icon: '⚡' },
    { name: 'Notion', desc: 'Sync deals and notes with your Notion workspace', status: 'Coming Soon', icon: '📝' },
    { name: 'Slack', desc: 'Get notifications about deal updates in Slack', status: 'Coming Soon', icon: '💬' },
  ];

  return (
    <PageLayout title="Integrations" subtitle="Connect Sponsy with your favorite tools">
      <div className="space-y-8">
        <div className="grid md:grid-cols-2 gap-4">
          {integrations.map((int, i) => (
            <div key={i} className="p-5 rounded-xl bg-neutral-900/50 border border-neutral-800">
              <div className="flex items-start gap-4">
                <span className="text-3xl">{int.icon}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-white">{int.name}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${int.status === 'Live' ? 'bg-lime-400/20 text-lime-400' : 'bg-neutral-700 text-neutral-400'}`}>
                      {int.status}
                    </span>
                  </div>
                  <p className="text-neutral-400 text-sm">{int.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="p-6 rounded-xl bg-neutral-900/50 border border-neutral-800 text-center">
          <h3 className="text-lg font-semibold text-white mb-2">Need a specific integration?</h3>
          <p className="text-neutral-400 text-sm mb-4">We're always adding new integrations based on creator feedback.</p>
          <a href="mailto:support@sponsy.app?subject=Integration Request" className="text-lime-400 hover:underline">
            Request an integration →
          </a>
        </div>
      </div>
    </PageLayout>
  );
}

// ============ CHANGELOG PAGE ============
function ChangelogPage() {
  const updates = [
    {
      version: '1.2.0',
      date: 'December 2025',
      title: 'E-Signatures & Legal Pages',
      changes: ['DocuSign integration for e-signatures', 'Privacy Policy and Terms of Service', 'Marketing website launch', 'Improved mobile responsiveness']
    },
    {
      version: '1.1.0',
      date: 'December 2025',
      title: 'Analytics & Media Kit',
      changes: ['Analytics dashboard', 'Media kit builder', 'PWA support for mobile', 'Keyboard shortcuts']
    },
    {
      version: '1.0.0',
      date: 'December 2025',
      title: 'Initial Launch',
      changes: ['Deal pipeline with drag-and-drop', 'Brand CRM with logo fetching', 'Invoice creation and tracking', 'Contract templates', 'Stripe payments integration', 'Google OAuth authentication']
    },
  ];

  return (
    <PageLayout title="Changelog" subtitle="What's new in Sponsy">
      <div className="space-y-8">
        {updates.map((update, i) => (
          <div key={i} className="relative pl-6 pb-8 border-l-2 border-neutral-800 last:pb-0">
            <div className="absolute -left-2 top-0 w-4 h-4 rounded-full bg-lime-400"></div>
            <div className="flex items-center gap-3 mb-2">
              <span className="px-2.5 py-0.5 rounded-full bg-neutral-800 text-neutral-300 text-xs font-mono">v{update.version}</span>
              <span className="text-neutral-500 text-sm">{update.date}</span>
            </div>
            <h2 className="text-xl font-semibold text-white mb-3">{update.title}</h2>
            <ul className="space-y-2">
              {update.changes.map((change, j) => (
                <li key={j} className="flex items-start gap-2 text-neutral-400 text-sm">
                  <span className="text-lime-400 mt-1">•</span>
                  {change}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </PageLayout>
  );
}

// ============ SECURITY PAGE ============
function SecurityPage() {
  return (
    <PageLayout title="Security" subtitle="How we protect your data">
      <div className="prose prose-invert max-w-none space-y-8">
        <section>
          <h2 className="text-2xl font-semibold text-white mb-4">Our Commitment</h2>
          <p className="text-neutral-300 leading-relaxed">
            Security is fundamental to everything we build. Your sponsorship deals, brand contacts, and financial
            information deserve the highest level of protection, and we're committed to providing exactly that.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-4">Infrastructure</h2>
          <div className="space-y-4">
            {[
              { title: 'Cloud Hosting', desc: 'Hosted on Vercel and Supabase with SOC 2 Type II compliance' },
              { title: 'Data Encryption', desc: '256-bit AES encryption at rest, TLS 1.3 in transit' },
              { title: 'Database Security', desc: 'PostgreSQL with row-level security policies' },
              { title: 'DDoS Protection', desc: 'Automatic DDoS mitigation through our CDN' },
            ].map((item, i) => (
              <div key={i} className="p-4 rounded-xl bg-neutral-900/50 border border-neutral-800">
                <h3 className="font-medium text-white mb-1">{item.title}</h3>
                <p className="text-neutral-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-4">Authentication</h2>
          <ul className="list-disc list-inside text-neutral-300 space-y-2">
            <li>Secure OAuth 2.0 with Google</li>
            <li>Password hashing with bcrypt</li>
            <li>Session tokens with automatic expiration</li>
            <li>Multi-factor authentication support (coming soon)</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-4">Payment Security</h2>
          <p className="text-neutral-300 leading-relaxed">
            All payment processing is handled by Stripe, a PCI Level 1 certified payment processor.
            We never store credit card numbers on our servers.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-4">Compliance</h2>
          <ul className="list-disc list-inside text-neutral-300 space-y-2">
            <li>Florida Digital Bill of Rights (FDBR) compliant</li>
            <li>COPPA compliant (no users under 13)</li>
            <li>FTC Act compliant</li>
            <li>SOC 2 Type II (via infrastructure providers)</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-4">Responsible Disclosure</h2>
          <p className="text-neutral-300 leading-relaxed">
            If you discover a security vulnerability, please report it responsibly to
            <a href="mailto:support@sponsy.app" className="text-lime-400 hover:underline ml-1">support@sponsy.app</a>.
            We appreciate the security research community and will acknowledge your contribution.
          </p>
        </section>
      </div>
    </PageLayout>
  );
}

// ============ FOOTER ============
function Footer() {
  const links = {
    Product: [
      { name: 'Features', href: '/#features' },
      { name: 'Pricing', href: '/#pricing' },
      { name: 'Integrations', href: '/integrations' },
      { name: 'Changelog', href: '/changelog' },
    ],
    Resources: [
      { name: 'Blog', href: '/blog' },
      { name: 'Help Center', href: '/help' },
      { name: 'Creator Guide', href: '/guide' },
      { name: 'Templates', href: '/templates' },
    ],
    Company: [
      { name: 'About', href: '/about' },
      { name: 'Careers', href: '/careers' },
      { name: 'Contact', href: '/contact' },
    ],
    Legal: [
      { name: 'Privacy', href: '/privacy' },
      { name: 'Terms', href: '/terms' },
      { name: 'Security', href: '/security' },
    ],
  };

  return (
    <footer className="bg-black border-t border-neutral-900 py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8 mb-10">
          <div className="col-span-2">
            <a href="/" className="flex items-center gap-2.5 mb-4">
              <Logo size={28} />
              <span className="text-base font-bold text-white">Sponsy</span>
            </a>
            <p className="text-neutral-500 text-sm mb-5 max-w-xs leading-relaxed">
              The all-in-one CRM built for content creators. Track deals, manage brands, grow your business.
            </p>
            <div className="flex gap-2">
              {[Twitter, Youtube, Instagram].map((SocialIcon, i) => (
                <a key={i} href="#" className="w-9 h-9 rounded-lg bg-neutral-900 hover:bg-neutral-800 flex items-center justify-center text-neutral-400 hover:text-white transition-colors">
                  <SocialIcon size={16} />
                </a>
              ))}
            </div>
          </div>
          
          {Object.entries(links).map(([title, items]) => (
            <div key={title}>
              <h4 className="font-semibold text-white mb-3 text-sm">{title}</h4>
              <ul className="space-y-2">
                {items.map((item, i) => (
                  <li key={i}>
                    <a href={item.href} className="text-neutral-500 hover:text-white text-sm transition-colors">{item.name}</a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="pt-8 border-t border-neutral-900 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-neutral-600 text-sm">© 2025 Sponsy. All rights reserved.</p>
          <p className="text-neutral-700 text-sm">Made with 💚 for creators worldwide</p>
        </div>
      </div>
    </footer>
  );
}

// ============ HOME PAGE ============
function HomePage() {
  return (
    <>
      <Navbar />
      <Hero />
      <Platforms />
      <Problem />
      <Features />
      <HowItWorks />
      <Comparison />
      <Testimonials />
      <Pricing />
      <FAQ />
      <FinalCTA />
      <Footer />
    </>
  );
}

// ============ MAIN APP WITH ROUTING ============
export default function SponsyWebsite() {
  const { path } = useRouter();
  
  // Render page based on path
  const renderPage = () => {
    switch (path) {
      // Legal pages
      case '/privacy':
        return <PrivacyPolicy />;
      case '/terms':
        return <TermsOfService />;
      case '/security':
        return <SecurityPage />;
      
      // Company pages
      case '/about':
        return <AboutPage />;
      case '/careers':
        return <CareersPage />;
      case '/contact':
        return <ContactPage />;
      
      // Resources pages
      case '/blog':
        return <BlogPage />;
      case '/help':
        return <HelpPage />;
      case '/guide':
        return <GuidePage />;
      case '/templates':
        return <TemplatesPage />;
      
      // Product pages
      case '/integrations':
        return <IntegrationsPage />;
      case '/changelog':
        return <ChangelogPage />;
      
      default:
        return <HomePage />;
    }
  };

  return (
    <div className="bg-black min-h-screen antialiased">
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee linear infinite;
        }
        html {
          scroll-behavior: smooth;
        }
        ::selection {
          background-color: #BFFF00;
          color: black;
        }
      `}</style>
      
      {renderPage()}
    </div>
  );
}
